import { ApiResponse, Rack, RacksResponse } from "../models/rack";

export interface FetchParams {
  page?: number;
  limit?: number;
  search?: string;
  filter?: Record<string, string | number | boolean>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const API_URL = "https://api-agamhive.agamvanigam.com/racks";

/** Get Auth Headers */
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("token");
  if (!token) {
    console.error("Token not found. Please log in again.");
    throw new Error("Token not found. Please log in again.");
  }
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

/** Fetch Racks */
export const getRacks = async (params: FetchParams = {}): Promise<ApiResponse<RacksResponse>> => {
  const token = localStorage.getItem("token");
  if (!token) return { error: "Unauthorized. Please log in." };

  try {
    const queryParams = new URLSearchParams({
      page: (params.page || 1).toString(),
      limit: (params.limit || 10).toString(),
      ...(params.search ? { search: params.search } : {}),
      ...(params.filter 
        ? Object.fromEntries(Object.entries(params.filter).map(([key, value]) => [key, String(value)]))
        : {}),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
    });
    
    console.log(`url for rack list : ${API_URL}?${queryParams}`);
    
    const response = await fetch(`${API_URL}?${queryParams}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const jsonResponse = await response.json();
    if (!response.ok) {
      return { error: jsonResponse.msg || "Failed to fetch racks" };
    }

    return {
      data: {
        racks: jsonResponse.data.racks || [],
        totalPages: Math.ceil(jsonResponse.data.total / (params.limit || 10)) || 1,
        total: jsonResponse.data.total,
      },
    };
  } catch (error) {
    return { error: "Network error. Please try again." };
  }
};

/** Create Rack */
export const createRack = async (rack: Rack): Promise<Rack> => {
  console.log("data send to api for create rack: ", rack);
  
  try {
    const response = await fetch(API_URL, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(rack),
    });

    console.log("Post method response:", response);

    if (!response.ok) {
      throw new Error(`Failed to create rack: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      rackName: data.rack?.rackName ?? data.rackName,
      status: data.rack?.status ?? data.status
    };
  } catch (error: any) {
    console.error("Error creating rack:", error.message);
    throw error; // Rethrow error so it can be caught in `handleSave`
  }
};

/** Update Rack */
export const updateRack = async (id: string, updatedRack: Partial<Rack>): Promise<Rack> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(updatedRack),
    });
    if (!response.ok) throw new Error(`Failed to update rack: ${response.statusText}`);

    return await response.json();
  } catch (error: any) {
    console.error("Error updating rack:", error.message);
    throw error;
  }
};

/** Delete Rack */
export const deleteRack = async (id: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, { 
      method: "DELETE", 
      headers: getAuthHeaders() 
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete rack: ${response.statusText}`);
    }
  } catch (error: any) {
    console.error("Error deleting rack:", error.message);
    throw error;
  }
};